import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useLocation, useNavigate, useSearchParams } from 'react-router'

import { Button } from '@/components/ui/button'
import { PageLoader } from '@/components/ui/PageLoader'
import { useHostGuard } from '@/lib/hostGuard'
import { startAnonymousApplication } from '@/models/anonymousApplication'
import { markSignedIn, redeemSignInTicket, useAuthStatus } from '@/models/auth'

/**
 * The marketing site's "Get your quotes" front door. A signed-out visitor gets a
 * stub account and is signed straight into the regular application chat, which
 * asks for everything else; anyone signed in already has an application.
 */
export function StartApplication() {
  // The session lives on the app host, so the marketing host hands the visitor over first.
  const { redirecting } = useHostGuard('app')
  const { data: authStatus, isError: authStatusFailed } = useAuthStatus()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const trade = searchParams.get('trade')
  // /application/workers/start starts a contractor application, whatever the flag says.
  const contractor = useLocation().pathname.toLowerCase().startsWith('/application/workers')
  const [failed, setFailed] = useState(false)
  const startedRef = useRef(false)

  useEffect(() => {
    if (redirecting || !authStatus || startedRef.current) return
    startedRef.current = true
    // Replace, not push: back from the chat must not build a second stub.
    const openChat = () => navigate('/application', { replace: true })
    if (authStatus.authenticated) {
      openChat()
      return
    }
    // Redeemed here rather than via /login-token, so one splash covers the whole hop.
    startAnonymousApplication(trade, window.location.search, contractor)
      .then(redeemSignInTicket)
      .then(() => {
        markSignedIn(queryClient)
        return openChat()
      })
      .catch(() => setFailed(true))
  }, [redirecting, authStatus, queryClient, navigate, trade, contractor])

  if (redirecting) return null

  if (failed || authStatusFailed) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-5.5 text-center">
        <p className="text-md text-ink-600">We couldn&apos;t start your application just now.</p>
        <Button onClick={() => window.location.reload()}>Try again</Button>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Start your application</title>
      </Helmet>
      <PageLoader persist label="Starting your application…" />
    </>
  )
}
